import { z } from 'zod';

// 'issuing' is a transient claim state: the issuance route atomically flips an
// 'approved' request to 'issuing' BEFORE talking to ConnectWise, so a concurrent
// click / second purchasing user cannot also proceed (see #284). It resolves to
// 'issued' on success or back to 'approved' if the CW call fails.
export type PORequestStatus = 'draft' | 'submitted' | 'approved' | 'issuing' | 'issued' | 'rejected';

export const PO_REQUEST_STATUSES: readonly PORequestStatus[] = [
  'draft', 'submitted', 'approved', 'issuing', 'issued', 'rejected',
];

const ALLOWED_TRANSITIONS: Record<PORequestStatus, PORequestStatus[]> = {
  // 'approved' is reachable directly from 'draft': survey-BOM-seeded requests are
  // created as drafts, and a PM "submits" one straight into the purchasing queue
  // (approved) — the same queue the direct create endpoints drop into (#297/#294).
  draft: ['submitted', 'approved', 'rejected'],
  submitted: ['approved', 'rejected'],
  approved: ['issuing', 'issued', 'rejected'],
  // While issuing, a request is mid-flight to CW — it may only complete ('issued')
  // or roll back ('approved'). It is deliberately NOT rejectable so a concurrent
  // reject cannot clobber an in-flight issuance.
  issuing: ['issued', 'approved'],
  issued: [],
  // A rejected request can be revised and put back into review (#303).
  rejected: ['submitted'],
};

/** Guard the PO-request lifecycle; only the listed transitions are legal. */
export function canTransitionPORequest(from: PORequestStatus, to: PORequestStatus): boolean {
  return ALLOWED_TRANSITIONS[from]?.includes(to) ?? false;
}

/** Equipment request amount = Σ (quantity × unitCost), rounded to cents. */
export function computeEquipmentAmount(lines: { quantity: number; unitCost: number }[]): number {
  const total = lines.reduce((sum, l) => sum + l.quantity * l.unitCost, 0);
  return Math.round(total * 100) / 100;
}

/** Sub-labor CW line encoding for progress billing: dollars as quantity, $1 unit cost, UOM Each (id 1). */
export function subLaborLine(input: { amount: number }): {
  quantity: number;
  unitCost: number;
  unitOfMeasureId: number;
} {
  return { quantity: input.amount, unitCost: 1, unitOfMeasureId: 1 };
}

/** Group lines into one bucket per final vendor (→ one CW PO per vendor). */
export function groupLineItemsByVendor<T extends { finalVendorId: number }>(lines: T[]): Map<number, T[]> {
  const groups = new Map<number, T[]>();
  for (const line of lines) {
    const bucket = groups.get(line.finalVendorId);
    if (bucket) bucket.push(line);
    else groups.set(line.finalVendorId, [line]);
  }
  return groups;
}

export const poShipToSchema = z.discriminatedUnion('mode', [
  z.object({ mode: z.literal('warehouse'), warehouseId: z.coerce.number().int().positive() }),
  z.object({
    mode: z.literal('dropship'),
    customerCompanyId: z.coerce.number().int().positive(),
    customerSiteId: z.coerce.number().int().positive(),
  }),
]);
export type POShipTo = z.infer<typeof poShipToSchema>;

export const equipmentPORequestSchema = z.object({
  lineItems: z.array(z.object({
    catalogItemId: z.number().int().positive(),
    qty: z.number().positive(),
    unitCost: z.number().min(0),
    description: z.string().min(1).max(1000),
    uomId: z.number().int().positive().optional(),
    suggestedVendorId: z.number().int().positive().optional(),
    requestedOverrideVendorId: z.number().int().positive().optional(),
  })).min(1),
  shipTo: poShipToSchema,
  notes: z.string().max(1000).optional(),
});
export type EquipmentPORequestInput = z.infer<typeof equipmentPORequestSchema>;

// Sub requests arrive as multipart form-data (values are strings) → coerce.
export const subPORequestSchema = z.object({
  subCompanyId: z.coerce.number().int().positive(),
  // Display name of the selected subcontractor, so the purchasing queue email can
  // name the sub instead of showing nothing (#781). Optional for back-compat.
  subCompanyName: z.string().trim().min(1).max(200).optional(),
  amount: z.coerce.number().positive(),
  description: z.string().max(1000).optional(),
});
export type SubPORequestInput = z.infer<typeof subPORequestSchema>;

// A "simple" (ad-hoc) request: no catalog BOM, no CW issuance. It is an
// approval-only record that flows through the same threshold gate + approval +
// resubmit as any other request (#303 — replaces the legacy PurchaseRequest).
// Vendor is picked from a CW typeahead (id + resolved name), never free-typed.
export const simplePORequestSchema = z.object({
  type: z.enum(['equipment', 'sub']),
  amount: z.coerce.number().positive(),
  vendorId: z.coerce.number().int().positive(),
  vendorName: z.string().min(1).max(200),
  description: z.string().min(1).max(500),
});
export type SimplePORequestInput = z.infer<typeof simplePORequestSchema>;

export const issuePOSchema = z.object({
  finalVendorByLineItemId: z.record(z.string(), z.number().int().positive()).optional(),
  termsId: z.number().int().positive().optional(),
  shipTo: poShipToSchema.optional(),
});
export type IssuePOInput = z.infer<typeof issuePOSchema>;
